import { createLoadProjects } from '../domain/projects/load-projects.js';
import {
  createCreateProject,
  createUpdateProject,
  createDeleteProject,
  createUploadProjectFiles,
} from '../domain/projects/manage-projects.js';
import { createSyncProjectsState } from '../domain/projects/sync-projects-state.js';

export class ProjectsService {
  constructor({
    projectsGateway,
    settingsService = null,
    documentRef = typeof window !== 'undefined' ? window.document : null,
    syncProjectsStateFactory = createSyncProjectsState,
    loadSettings,
    saveSettings,
    setLastProjects,
    dispatchProjectsUpdated,
  } = {}) {
    this.projectsGateway = projectsGateway || null;
    this.settingsService = settingsService || null;
    this.documentRef = documentRef || null;
    this.lastProjectsSnapshot = [];
    this.lastProjectsEventDetail = null;
    this.lastProjectsSettings = null;

    this.loadProjectsUseCase = createLoadProjects({
      loaders: [
        async () => {
          if (!this.projectsGateway || typeof this.projectsGateway.list !== 'function') {
            return null;
          }
          return this.projectsGateway.list();
        },
      ],
    });

    this.createProjectUseCase = createCreateProject({
      saveProject: (payload) => {
        if (!this.projectsGateway || typeof this.projectsGateway.create !== 'function') {
          throw new Error('Projects gateway unavailable');
        }
        return this.projectsGateway.create(payload);
      },
    });

    this.updateProjectUseCase = createUpdateProject({
      updateProject: (teamId, payload) => {
        if (!this.projectsGateway || typeof this.projectsGateway.update !== 'function') {
          throw new Error('Projects gateway unavailable');
        }
        return this.projectsGateway.update(teamId, payload);
      },
    });

    this.deleteProjectUseCase = createDeleteProject({
      deleteProject: (teamId) => {
        if (!this.projectsGateway || typeof this.projectsGateway.delete !== 'function') {
          throw new Error('Projects gateway unavailable');
        }
        return this.projectsGateway.delete(teamId);
      },
    });

    this.uploadProjectFilesUseCase = createUploadProjectFiles({
      uploadFiles: (teamId, files) => {
        if (!this.projectsGateway || typeof this.projectsGateway.uploadFiles !== 'function') {
          throw new Error('Projects gateway unavailable');
        }
        return this.projectsGateway.uploadFiles(teamId, files);
      },
    });

    this.syncProjectsStateUseCase = this.#createSyncProjectsState({
      syncProjectsStateFactory,
      loadSettings,
      saveSettings,
      setLastProjects,
      dispatchProjectsUpdated,
    });
  }

  async listProjects() {
    const startedAt = Date.now();
    try {
      const result = await this.loadProjectsUseCase();
      this.#recordTelemetry({
        action: 'list',
        outcome: 'success',
        startedAt,
        count: Array.isArray(result) ? result.length : undefined,
      });
      return Array.isArray(result) ? result : [];
    } catch (error) {
      this.#recordTelemetry({ action: 'list', outcome: 'error', startedAt, error });
      throw error;
    }
  }

  async createProject(request = {}) {
    const startedAt = Date.now();
    try {
      const response = await this.createProjectUseCase(request);
      this.#recordTelemetry({ action: 'create', outcome: 'success', startedAt });
      return response;
    } catch (error) {
      this.#recordTelemetry({ action: 'create', outcome: 'error', startedAt, error });
      throw error;
    }
  }

  async updateProject(teamId, data = {}) {
    const startedAt = Date.now();
    try {
      const response = await this.updateProjectUseCase({ teamId, data });
      this.#recordTelemetry({ action: 'update', outcome: 'success', startedAt });
      return response;
    } catch (error) {
      this.#recordTelemetry({ action: 'update', outcome: 'error', startedAt, error });
      throw error;
    }
  }

  async listProjectFiles(teamId) {
    if (!teamId) {
      throw new Error('Project ID is required');
    }
    if (!this.projectsGateway || typeof this.projectsGateway.listProjectFiles !== 'function') {
      throw new Error('Projects gateway unavailable');
    }
    const files = await this.projectsGateway.listProjectFiles(teamId);
    return Array.isArray(files) ? files : [];
  }

  async deleteProject(teamId) {
    const startedAt = Date.now();
    try {
      const response = await this.deleteProjectUseCase({ teamId });
      this.#recordTelemetry({ action: 'delete', outcome: 'success', startedAt });
      return response;
    } catch (error) {
      this.#recordTelemetry({ action: 'delete', outcome: 'error', startedAt, error });
      throw error;
    }
  }

  async uploadProjectFiles(teamId, files = []) {
    const startedAt = Date.now();
    try {
      const response = await this.uploadProjectFilesUseCase({ teamId, files });
      this.#recordTelemetry({ action: 'upload', outcome: 'success', startedAt });
      return response;
    } catch (error) {
      this.#recordTelemetry({ action: 'upload', outcome: 'error', startedAt, error });
      throw error;
    }
  }

  async deleteProjectFile(teamId, fileId) {
    if (!teamId || !fileId) {
      throw new Error('Project and file IDs are required');
    }
    if (!this.projectsGateway || typeof this.projectsGateway.deleteProjectFile !== 'function') {
      throw new Error('Projects gateway unavailable');
    }
    await this.projectsGateway.deleteProjectFile(teamId, fileId);
    return undefined;
  }

  syncProjectsState(projects, viewModel, meta = {}) {
    const options = { ...(meta || {}) };
    if (!options.source) {
      options.source = 'projects-service';
    }
    if (typeof viewModel !== 'undefined') {
      options.viewModel = viewModel;
    }

    if (!this.syncProjectsStateUseCase) {
      const snapshot = this.#cloneProjects(projects);
      this.#setLastProjectsFallback(snapshot);
      const detail = this.#buildProjectsDetail(snapshot, options);
      this.#dispatchProjectsUpdated(detail, null);
      return this.#captureSyncResult({ projects: snapshot, settings: null, detail });
    }

    const result = this.syncProjectsStateUseCase(projects, options);
    return this.#captureSyncResult(result);
  }

  #captureSyncResult(result) {
    const hasObjectResult = result && typeof result === 'object' && !Array.isArray(result);
    const projectsSource = hasObjectResult ? result.projects : result;
    const projects = this.#cloneProjects(projectsSource);

    this.lastProjectsSnapshot = projects;

    if (hasObjectResult && result.settings && typeof result.settings === 'object') {
      this.lastProjectsSettings = this.#cloneSettings(result.settings);
    }

    if (hasObjectResult && result.detail) {
      const detailSnapshot = this.#cloneDetail(result.detail);
      if (detailSnapshot) {
        this.lastProjectsEventDetail = detailSnapshot;
      }
    }

    return {
      projects: this.#cloneProjects(this.lastProjectsSnapshot),
      settings: this.lastProjectsSettings ? this.#cloneSettings(this.lastProjectsSettings) : null,
      detail: this.lastProjectsEventDetail ? this.#cloneDetail(this.lastProjectsEventDetail) : null,
    };
  }

  #buildProjectsDetail(projects, { source, viewModel, activeTeamId } = {}) {
    if (!Array.isArray(projects)) {
      return null;
    }
    const detail = {
      items: this.#cloneProjects(projects),
      source: source || 'projects-service',
    };
    if (typeof activeTeamId !== 'undefined') {
      detail.activeTeamId = activeTeamId ?? null;
    }
    if (typeof viewModel !== 'undefined') {
      detail.viewModel = viewModel;
    }
    return detail;
  }

  #cloneDetail(detail) {
    if (!detail || typeof detail !== 'object') {
      return null;
    }
    const clone = { ...detail };
    if (Array.isArray(detail.items)) {
      clone.items = this.#cloneProjects(detail.items);
    }
    if (detail.viewModel && typeof detail.viewModel === 'object') {
      clone.viewModel = { ...detail.viewModel };
    }
    return clone;
  }

  #cloneSettings(settings) {
    if (!settings || typeof settings !== 'object') {
      return null;
    }
    return {
      ...settings,
      projects: this.#cloneProjects(settings.projects),
      teams: this.#cloneMixedArray(settings.teams),
      library: this.#cloneMixedArray(settings.library),
      last: { ...(settings.last || {}) },
      prefs: { ...(settings.prefs || {}) },
    };
  }

  #cloneMixedArray(items) {
    if (!Array.isArray(items)) {
      return [];
    }
    return items.map((item) => (item && typeof item === 'object' ? { ...item } : item));
  }

  #cloneProjects(projects) {
    if (!Array.isArray(projects)) {
      return [];
    }
    return projects.map((project) => ({ ...(project || {}) }));
  }

  #createSyncProjectsState({
    syncProjectsStateFactory,
    loadSettings,
    saveSettings,
    setLastProjects,
    dispatchProjectsUpdated,
  }) {
    if (typeof syncProjectsStateFactory !== 'function') {
      return null;
    }

    const resolvedLoadSettings = this.#resolveLoadSettings(loadSettings);
    const resolvedSaveSettings = this.#resolveSaveSettings(saveSettings);
    const resolvedSetLastProjects = this.#resolveSetLastProjects(setLastProjects);
    const resolvedDispatch = this.#resolveDispatch(dispatchProjectsUpdated);

    return syncProjectsStateFactory({
      loadSettings: resolvedLoadSettings,
      saveSettings: resolvedSaveSettings,
      setLastProjects: resolvedSetLastProjects,
      dispatchProjectsUpdated: resolvedDispatch,
      defaultSource: 'projects-service',
    });
  }

  #resolveLoadSettings(overridden) {
    if (typeof overridden === 'function') {
      return overridden;
    }
    if (this.settingsService && typeof this.settingsService.get === 'function') {
      return () => this.settingsService.get();
    }
    return undefined;
  }

  #resolveSaveSettings(overridden) {
    if (typeof overridden === 'function') {
      return overridden;
    }
    if (this.settingsService && typeof this.settingsService.save === 'function') {
      return (next) => this.settingsService.save(next);
    }
    return undefined;
  }

  #resolveSetLastProjects(overridden) {
    if (typeof overridden === 'function') {
      return overridden;
    }
    return (projects) => this.#setLastProjectsFallback(projects);
  }

  #resolveDispatch(overridden) {
    if (typeof overridden === 'function') {
      return overridden;
    }
    return (detail, settings) => this.#dispatchProjectsUpdated(detail, settings);
  }

  #setLastProjectsFallback(projects) {
    const list = Array.isArray(projects) ? projects : [];
    this.lastProjectsSnapshot = list.map((project) => ({ ...(project || {}) }));
    if (typeof window !== 'undefined') {
      try {
        window.__lastProjects = this.lastProjectsSnapshot.map((project) => ({ ...project }));
      } catch (error) {
        // ignore window assignment errors
      }
    }
  }

  #dispatchProjectsUpdated(detail, settings) {
    if (settings && typeof settings === 'object') {
      this.lastProjectsSettings = this.#cloneSettings(settings);
    } else if (settings === null) {
      this.lastProjectsSettings = null;
    }

    const clonedDetail = this.#cloneDetail(detail);
    if (clonedDetail) {
      this.lastProjectsEventDetail = clonedDetail;
    }

    if (!clonedDetail) {
      return;
    }

    const doc = this.documentRef;
    if (!doc || typeof doc.dispatchEvent !== 'function') {
      return;
    }
    const EventCtor = typeof CustomEvent === 'function' ? CustomEvent : null;
    if (!EventCtor) {
      return;
    }
    try {
      doc.dispatchEvent(new EventCtor('projects:updated', { detail: this.#cloneDetail(clonedDetail) }));
    } catch (error) {
      // ignore dispatch failures to keep sync resilient
    }
  }

  #recordTelemetry({ action, outcome, startedAt, error = null, count }) {
    if (typeof window === 'undefined') {
      return;
    }
    const debug = window.Debug || null;
    if (!debug || typeof debug.recordProjectsTelemetry !== 'function') {
      return;
    }
    const durationMs = typeof startedAt === 'number' ? Date.now() - startedAt : undefined;
    debug.recordProjectsTelemetry({
      action,
      outcome,
      durationMs,
      count,
      errorMessage: error && error.message ? error.message : error ? String(error) : undefined,
    });
  }
}

if (typeof window !== 'undefined') {
  window.AgileProServices = window.AgileProServices || {};
  window.AgileProServices.ProjectsService = ProjectsService;
}





